// frontend/src/api.js
const defaultApiBase = (() => {
  // In the browser: use the same origin + /api
  // e.g. https://cbcfx.site/api or https://gtrust.cbcfx.site/api
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin.replace(/\/$/, "")}/api`;
  }

  // Fallback when there's no window (tests, Node scripts, etc.)
  return "http://127.0.0.1:5000/api";
})();

// Optional override via env, but normally you don't need this in prod
const configuredApiBase = import.meta.env.VITE_API_BASE_URL || defaultApiBase;

export const API = configuredApiBase.replace(/\/$/, "");
export const API_ORIGIN = API.replace(/\/api$/, "");

function portalUrl(path = "") {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${API}${suffix}`;
}
const API_TOKEN = import.meta.env.VITE_API_TOKEN || "";
const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN || "";

// ✅ PORTAL LOGIN URL - Always use absolute URL for cross-subdomain redirects
const PORTAL_LOGIN_URL = (() => {
  const isDev = import.meta.env.DEV;
  if (isDev) return "http://localhost:5173/portal/login";
  const domain = import.meta.env.VITE_PORTAL_ROOT_DOMAIN || "portal.cbcfx.site";
  return `https://${domain}/portal/login`;
})();

let portalTokenProvider = null;
let portalTenantSlugProvider = null;
let portalTenantIdProvider = null;
let portalSignOutCallback = null; // ✅ ADD THIS LINE

export function setPortalTokenProvider(provider) {
  portalTokenProvider = provider;
  console.log("[API] Token provider configured");
}

export function setPortalTenantSlugProvider(provider) {
  portalTenantSlugProvider = provider;
  console.log("[API] Tenant slug provider configured");
}

export function setPortalTenantIdProvider(provider) {
  portalTenantIdProvider = provider;
  console.log("[API] Tenant ID provider configured");
}

/**
 * ✅ NEW: Configure sign-out callback for when session expires
 */
export function setPortalSignOutCallback(callback) {
  portalSignOutCallback = callback;
  console.log("[API] Sign-out callback configured");
}

/* ---------------------------- device identification ---------------------------- */

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const part of cookies) {
    const [rawKey, ...rawVal] = part.split("=");
    if (!rawKey) continue;
    const key = rawKey.trim();
    if (!key) continue;
    if (key === name) {
      const value = rawVal.length ? rawVal.join("=").trim() : "";
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return null;
}

function setCookie(name, value, options = {}) {
  if (typeof document === "undefined") return;

  const {
    maxAge, // seconds
    domain, // e.g. ".cbcfx.site"
    path = "/",
    sameSite = "Lax",
    secure,
  } = options;

  const parts = [`${name}=${encodeURIComponent(value)}`];

  if (Number.isFinite(maxAge)) {
    parts.push(`Max-Age=${Math.floor(maxAge)}`);
  }

  if (path) {
    parts.push(`Path=${path}`);
  }

  if (domain) {
    parts.push(`Domain=${domain}`);
  }

  if (sameSite) {
    parts.push(`SameSite=${sameSite}`);
  }

  if (secure) {
    parts.push("Secure");
  }

  document.cookie = parts.join("; ");
}

function getOrCreateDeviceId() {
  const COOKIE_NAME = "cbcfx_device_id";
  const STORAGE_KEY = "cbcfx_device_id";

  // Non-browser contexts (tests, SSR): just return a throwaway ID
  if (typeof window === "undefined") {
    return crypto.randomUUID();
  }

  // 1) Try cookie (shared across subdomains)
  let deviceId = getCookie(COOKIE_NAME);

  // 2) One-time migration from localStorage → cookie (per-origin)
  try {
    if (!deviceId && typeof localStorage !== "undefined") {
      const legacy = localStorage.getItem(STORAGE_KEY);
      if (legacy) {
        deviceId = legacy;
      }
    }
  } catch {
    // ignore
  }

  // 3) If still nothing, generate a new one
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    console.log("[API] Generated new device ID:", deviceId);
  }

  // 4) Persist to cookie (and keep localStorage in sync for safety)
  try {
    const host = window.location.hostname || "";
    const isProdLike = host.endsWith(".cbcfx.site");
    const isSecure = window.location.protocol === "https:";

    const cookieOptions = {
      maxAge: 60 * 60 * 24 * 365, // 1 year
      path: "/",
      sameSite: "Lax",
    };

    // Share cookie across all subdomains in prod
    if (isProdLike) {
      cookieOptions.domain = ".cbcfx.site";
    }

    // Only set Secure in HTTPS (works in prod; localhost will normally be http)
    if (isSecure) {
      cookieOptions.secure = true;
    }

    setCookie(COOKIE_NAME, deviceId, cookieOptions);

    // Keep legacy storage updated (no functional change, just consistency)
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, deviceId);
      }
    } catch {
      // ignore
    }
  } catch (err) {
    console.warn("[API] Failed to persist device ID cookie:", err);
  }

  return deviceId;
}

/**
 * Generate or retrieve persistent device ID for session management.
 * Stored in localStorage - shared across all tabs in same browser.
 * Incognito/private mode gets separate device ID (expected behavior).
 */
function getDeviceId() {
  try {
    return getOrCreateDeviceId();
  } catch (err) {
    console.warn("[API] Failed to get device ID, generating ephemeral one", err);
    return crypto.randomUUID();
  }
}

/* ---------------------------- core fetch helpers ---------------------------- */
export async function apiFetch(url, opts = {}) {
  const { admin, portal, timeout, credentials, retry = true, ...rest } = opts;

  // Build headers
  const headers = new Headers(rest.headers || {});

  // ✅ Always include device ID for session tracking
  try {
    const deviceId = getDeviceId();
    headers.set("X-Device-ID", deviceId);
  } catch (err) {
    console.warn("[apiFetch] Could not set device ID:", err);
  }

  if (portal) {
    if (typeof portalTokenProvider !== "function") {
      const err = new Error("Portal auth not configured - token provider is not set");
      console.error("[apiFetch] Portal auth not configured");
      throw err;
    }

    // Get token with validation
    const token = await portalTokenProvider();
    if (!token) {
      const err = new Error("Portal token is null - authentication may have failed");
      console.error("[apiFetch] Token is null");
      throw err;
    }

    console.log("[apiFetch] Setting Authorization header for portal request");
    headers.set("Authorization", `Bearer ${token}`);

    // Set tenant slug
    if (typeof portalTenantSlugProvider === "function") {
      try {
        const slug = await portalTenantSlugProvider();
        if (slug) {
          console.log("[apiFetch] Setting X-Tenant-Slug:", slug);
          headers.set("X-Tenant-Slug", String(slug));
        }
      } catch (err) {
        console.warn("portal tenant slug provider failed", err?.message || err);
      }
    }

    // Set tenant ID
    if (typeof portalTenantIdProvider === "function") {
      try {
        const tenantId = await portalTenantIdProvider();
        if (tenantId) {
          console.log("[apiFetch] Setting X-Tenant-Id:", tenantId);
          headers.set("X-Tenant-Id", String(tenantId));
        }
      } catch (err) {
        console.warn("portal tenant ID provider failed", err?.message || err);
      }
    }
  } else {
    const token = admin ? ADMIN_TOKEN : API_TOKEN;
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const controller = new AbortController();
  const timeoutMs = timeout ?? 15000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    console.log(`[apiFetch] ${rest.method || "GET"} ${url}`);
    const response = await fetch(url, {
      ...rest,
      headers,
      signal: controller.signal,
      credentials: credentials ?? "include",
    });

    console.log(`[apiFetch] Response: ${response.status} ${response.statusText}`);

    // ✅ Handle session expired (kicked by another device)
    // 401 = session expired after being kicked
    // 403 = session_inactive (trying to use bootstrap with old token)
    if (response.status === 401 || response.status === 403) {
      try {
        const errorData = await response.clone().json();
        if (errorData.title === "session_expired" || errorData.title === "session_kicked" || errorData.title === "session_inactive") {
          console.warn(`[apiFetch] ${errorData.title}: ${errorData.detail || "Session was ended on another device"}`);

          // ✅ Sign out of Clerk FIRST (Netflix/AWS/Google pattern)
          if (typeof portalSignOutCallback === "function") {
            try {
              console.log("[apiFetch] Calling sign-out callback...");
              await portalSignOutCallback();
              console.log("[apiFetch] Sign-out complete");
            } catch (signOutErr) {
              console.error("[apiFetch] Sign-out callback failed:", signOutErr);
            }
          }

          // Show user-friendly message
          const message = errorData.detail || "Your session was ended because you logged in from another device. Please log in again.";
          alert(message);

          // ✅ FIXED: Use absolute URL instead of relative URL
          window.location.href = PORTAL_LOGIN_URL;
          throw new Error(errorData.title);
        }
      } catch (jsonErr) {
        // Not a JSON response or different error - continue with retry logic
        if (jsonErr?.message === "session_expired" || jsonErr?.message === "session_inactive") {
          throw jsonErr; // Re-throw if we already handled it above
        }
      }

      // If 401 and this is a portal request with retry enabled, try refreshing token
      if (response.status === 401 && portal && retry && typeof portalTokenProvider === "function") {
        console.warn("[apiFetch] Got 401, attempting token refresh and retry...");
        // Wait a bit for token to refresh
        await new Promise((resolve) => setTimeout(resolve, 500));
        // Retry once with a fresh token
        return await apiFetch(url, { ...opts, retry: false });
      }
    }

    return response;
  } catch (err) {
    if (err.name === "AbortError") {
      console.error("[apiFetch] Request timed out");
      throw new Error("Request timed out");
    }
    console.error("[apiFetch] Network error:", err);
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchJSON(url, { json, body, ...opts } = {}) {
  const init = { ...opts };
  if (json !== undefined) {
    init.headers = { "Content-Type": "application/json", ...(init.headers || {}) };
    init.body = JSON.stringify(json);
  } else if (body !== undefined) {
    init.body = body;
  }

  let res;
  try {
    res = await apiFetch(url, init);
  } catch (e) {
    console.error("[fetchJSON] apiFetch failed:", e);
    throw new Error(e.message || "Network error");
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    console.error("[fetchJSON] Request failed:", {
      status: res.status,
      url,
      error: errorData,
    });

    const error = new Error(errorData.error || errorData.message || errorData.title || `Request failed (${res.status})`);
    error.status = res.status;
    error.data = errorData;
    throw error;
  }

  return res.json();
}

export async function fetchOK(url, opts = {}) {
  const { json, body, ...rest } = opts;
  const init = { ...rest };
  if (json !== undefined) {
    init.headers = { "Content-Type": "application/json", ...(init.headers || {}) };
    init.body = JSON.stringify(json);
  } else if (body !== undefined) {
    init.body = body;
  }

  let res;
  try {
    res = await apiFetch(url, init);
  } catch (e) {
    throw new Error(e.message || "Network error");
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return res.json().catch(() => ({}));
}

export function toFormData(obj = {}) {
  if (obj instanceof FormData) return obj;
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") fd.append(k, v);
  });
  return fd;
}

export const evaluateCompany = (id) => fetchJSON(`${API}/companies/${id}/evaluate`, { method: "GET", admin: true });

// Update company status (supports both string and structured payload)
export const setCompanyStatus = (id, statusOrPayload) => {
  const payload =
    typeof statusOrPayload === "string"
      ? { status: statusOrPayload }
      : statusOrPayload && typeof statusOrPayload === "object"
        ? statusOrPayload
        : { status: String(statusOrPayload || "") };

  return fetchJSON(`${API}/companies/${id}/status`, {
    method: "PATCH",
    json: payload,
    admin: true,
  });
};

// Fetch structured status history for a company
export const fetchCompanyStatusHistory = (id) =>
  fetchJSON(`${API}/companies/${id}/status-history`, {
    method: "GET",
    admin: true,
  });

/* ---------------------------------- files ---------------------------------- */
/*
export async function getSignedFileUrl(key) {
  const res = await apiFetch(`${API}/files/sign?key=${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error("Failed to sign file URL");
  const { url } = await res.json();
  return url;
}
  */

/* --------------------------------- alerts --------------------------------- */
export const getAlerts = (windowDays = 30) => fetchJSON(`${API}/alerts?windowDays=${encodeURIComponent(windowDays)}`);

export const snoozeAlert = (alertId, duration) =>
  fetchJSON(`${API}/alerts/${encodeURIComponent(alertId)}/snooze`, {
    method: "POST",
    json: { duration },
    admin: true,
  });

/* ---------------------------------- search --------------------------------- */
const toSearchParams = (input = {}) => {
  if (input instanceof URLSearchParams) return input;
  if (typeof input === "string") return new URLSearchParams(input);

  const params = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== "") {
          params.append(key, String(entry));
        }
      });
    } else if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  return params;
};

export const search = (params = {}) => {
  const qs = toSearchParams(params);
  return fetchJSON(`${API}/search?${qs.toString()}`, {
    portal: true,
  });
};

/* --------------------------- import / export jobs -------------------------- */
export async function importDatabaseCustomers(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await apiFetch(`${API}/import/database-customers`, { method: "POST", body: fd, admin: true });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Import failed (${res.status})`);
  return res.json();
}

export async function importFinalUpdated(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await apiFetch(`${API}/import/final-updated`, { method: "POST", body: fd, admin: true });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Enrich failed (${res.status})`);
  return res.json();
}

export async function downloadFinalUpdated() {
  const res = await apiFetch(`${API}/export/final-updated.xlsx`, { portal: true });
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `Export failed (${res.status})`);
  return res.blob();
}

export const fileUrl = (pathOrKey) => `${API_ORIGIN}/api/files/${encodeURIComponent(pathOrKey)}`;

/* --------------------------------- portal --------------------------------- */
export const fetchSessionBootstrap = () => fetchJSON(portalUrl("/session/bootstrap"), { portal: true });

export const fetchPortalMe = () => fetchJSON(portalUrl("/me"), { portal: true });

export const fetchPortalCompanies = () => fetchJSON(portalUrl("/portal/companies"), { portal: true });

export const fetchPortalCompany = (id) => fetchJSON(portalUrl(`/portal/companies/${encodeURIComponent(id)}`), { portal: true });

export const fetchImpersonationSession = () => fetchJSON(`${API}/admin/impersonate`, { admin: true });

export const startImpersonationSession = (tenantId, userId) =>
  fetchJSON(`${API}/admin/impersonate`, {
    method: "POST",
    json: { tenantId, userId },
    admin: true,
  });

export const stopImpersonationSession = () => fetchJSON(`${API}/admin/impersonate`, { method: "DELETE", admin: true });

export const fetchAuditLog = (params = {}) => {
  const query = new URLSearchParams();
  if (params.limit) {
    query.set("limit", String(params.limit));
  }
  if (params.tenantId) {
    query.set("tenantId", String(params.tenantId));
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return fetchJSON(`${API}/admin/audit-log${suffix}`, { admin: true });
};

export const fetchOperatorTenants = (params = {}) => {
  const query = new URLSearchParams();
  if (params.status) {
    query.set("status", params.status);
  }
  if (params.search) {
    query.set("search", params.search);
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return fetchJSON(`${API}/admin/tenants${suffix}`, { admin: true });
};

export const createOperatorTenant = (payload) =>
  fetchJSON(`${API}/admin/tenants`, {
    method: "POST",
    json: payload,
    admin: true,
  });

export const updateOperatorTenant = (tenantId, payload) =>
  fetchJSON(`${API}/admin/tenants/${encodeURIComponent(tenantId)}`, {
    method: "PATCH",
    json: payload,
    admin: true,
  });

export const createClerkOrgForTenant = (tenantId, payload) =>
  fetchJSON(`${API}/admin/tenants/${encodeURIComponent(tenantId)}/clerk`, {
    method: "POST",
    json: payload,
    admin: true,
  });

// RIGHT:
export const inviteTenantMembers = (tenantId, payload) =>
  fetchJSON(`${API}/admin/tenants/${encodeURIComponent(tenantId)}/invitations`, {
    method: "POST",
    json: payload,
    portal: true, // ← FIXED! Use Clerk auth like portal route!
  });

export const createPortalInvitations = async (emails) => {
  const res = await apiFetch(portalUrl("/admin/invitations"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emails }),
    portal: true,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || data.message || `Request failed (${res.status})`);
    error.status = res.status;
    error.data = data;
    throw error;
  }
  return data;
};

export const fetchPortalTenantMembers = (slug) => fetchJSON(portalUrl(`/portal/tenants/${encodeURIComponent(slug)}/members`), { portal: true });

export const updatePortalTenantMemberRole = (slug, userId, role) =>
  fetchJSON(portalUrl(`/portal/tenants/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`), {
    method: "PATCH",
    json: { role },
    portal: true,
  });

export const fetchPortalCompanyDocs = (id) => fetchJSON(portalUrl(`/portal/companies/${encodeURIComponent(id)}/docs`), { portal: true });

export const uploadPortalDocument = (id, data) =>
  fetchJSON(portalUrl(`/portal/companies/${encodeURIComponent(id)}/docs`), {
    method: "POST",
    body: data,
    portal: true,
  });

/* export const signPortalFile = (key) => fetchJSON(portalUrl(`/files/sign?key=${encodeURIComponent(key)}`), { portal: true }); */

/* ----------------------------- browse filters ----------------------------- */
export const fetchCompanyFilters = () =>
  fetchJSON(`${API}/browse/company-filters`, {
    portal: true,
  });

/* ----------------------------- intake & reviews ---------------------------- */
export const createIntake = async (payload) => {
  try {
    return await fetchJSON(`${API}/intake`, { method: "POST", json: payload, portal: true });
  } catch (error) {
    if (error.status === 422 && error.data?.error === "docs_missing") {
      const missing = error.data.missing || [];
      const newError = new Error(`Missing required documents: ${missing.join(", ")}`);
      newError.status = error.status;
      newError.missingDocs = missing;
      throw newError;
    }
    throw error;
  }
};

export const fetchReviewSchema = () => fetchJSON(`${API}/reviews/schema`);
export const updateReview = (id, payload) => fetchJSON(`${API}/reviews/${id}`, { method: "PATCH", json: payload });
export const getReview = (id) => fetchJSON(`${API}/reviews/${id}`);
export const suggestCompanies = (q) => fetchJSON(`${API}/companies/suggest?q=${encodeURIComponent(q)}`);
export const fetchMetaOptions = () => fetchJSON(`${API}/meta/options`);
export const getDocStatuses = () => fetchJSON(`${API}/meta/doc-statuses`);
export const getLegalForms = () => fetchJSON(`${API}/meta/legal-forms`);
export const getSectors = () => fetchJSON(`${API}/meta/sectors`);

export const createReview = (payload) => fetchJSON(`${API}/reviews`, { method: "POST", json: payload });

/* ------------------------ company status / profile ------------------------ */
export const approveCompany = (id, status = "active") =>
  (async () => {
    const res = await apiFetch(`${API}/companies/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
      admin: true,
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 422) {
      const msg = data.title;
      if (msg === "docs_missing") {
        return { ok: false, error: "docs_missing", missing: data.errors || [] };
      }
      if (msg === "ubo_share_total_exceeds_100") {
        return { ok: false, error: msg, total: data.total };
      }
      return { ok: false, error: msg };
    }
    if (!res.ok) throw new Error(data.title || `Request failed (${res.status})`);
    return data;
  })();

export const updateCompany = (id, data) => fetchJSON(`${API}/companies/${id}`, { method: "PATCH", json: data, admin: true });

export const updateCompanyProfile = (id, payload) =>
  fetchJSON(`${API}/companies/${id}/profile`, {
    method: "PATCH",
    json: { ...payload, total_assets_currency: payload.total_assets_currency ?? null },
    admin: true,
  });

/* ------------------------------ company reads ----------------------------- */
export const getCompany = (id) => fetchJSON(`${API}/companies/${id}`);
export const getCompanyRoles = (id) => fetchJSON(`${API}/companies/${id}/roles`);
export const getCompanyDocs = (id) => fetchJSON(`${API}/companies/${id}/docs`);
export const getCompanyReviews = (id) => fetchJSON(`${API}/companies/${id}/reviews`);

/* ------------------------- compliance extras (new) ------------------------- */
export const getCompanyTaxYears = (id) => fetchJSON(`${API}/companies/${id}/tax-years`);
export const addCompanyTaxYear = (id, payload) => fetchJSON(`${API}/companies/${id}/tax-years`, { method: "POST", json: payload, admin: true });
export const updateCompanyTaxYear = (id, tyId, payload) => fetchJSON(`${API}/companies/${id}/tax-years/${tyId}`, { method: "PATCH", json: payload, admin: true });

export const getDeviezenPermits = (id) => fetchJSON(`${API}/companies/${id}/deviezen-permits`);
export const addDeviezenPermit = (id, payload) => fetchJSON(`${API}/companies/${id}/deviezen-permits`, { method: "POST", json: payload, admin: true });
export const updateDeviezenPermit = (id, permitId, payload) => fetchJSON(`${API}/companies/${id}/deviezen-permits/${permitId}`, { method: "PATCH", json: payload, admin: true });

export const getScreenings = (id) => fetchJSON(`${API}/companies/${id}/screenings`);
export const addScreening = (id, payload) => fetchJSON(`${API}/companies/${id}/screenings`, { method: "POST", json: payload, admin: true });
export const updateScreening = (id, scrId, payload) => fetchJSON(`${API}/companies/${id}/screenings/${scrId}`, { method: "PATCH", json: payload, admin: true });

export const getReviewSnapshot = (id) => fetchJSON(`${API}/companies/${id}/review-snapshot`);

/* ---------------------------- document requirements ---------------------------- */
export const getDocRequirements = (params) => {
  const qs = new URLSearchParams(params);
  return fetchJSON(`${API}/docs/requirements?${qs.toString()}`);
};

/* ------------------------------- roles / docs ------------------------------ */
export const addCompanyRole = (id, payload) => fetchJSON(`${API}/companies/${id}/roles`, { method: "POST", json: payload, admin: true });

export const addCompanyDoc = (id, payload, personId) =>
  fetchJSON(`${API}/companies/${id}/docs${personId ? `?person_id=${personId}` : ""}`, {
    method: "POST",
    body: toFormData(payload),
    admin: true,
  });

export const updateCompanyDoc = (id, docId, fdOrObj) =>
  fetchJSON(`${API}/companies/${id}/docs/${docId}`, {
    method: "PATCH",
    body: toFormData(fdOrObj),
    admin: true,
  });

export const deleteCompanyDoc = (id, docId) => fetchOK(`${API}/companies/${id}/docs/${docId}`, { method: "DELETE", admin: true });

export const updateCompanyRole = (id, roleId, payload) => fetchJSON(`${API}/companies/${id}/roles/${roleId}`, { method: "PATCH", json: payload, admin: true });

export const deleteCompanyRole = (id, roleId) => fetchOK(`${API}/companies/${id}/roles/${roleId}`, { method: "DELETE", admin: true });

export const updateCompanyReview = (id, revId, payload) => fetchJSON(`${API}/companies/${id}/reviews/${revId}`, { method: "PATCH", json: payload, admin: true });

export const deleteCompanyReview = (id, revId) => fetchOK(`${API}/companies/${id}/reviews/${revId}`, { method: "DELETE", admin: true });

/* --------------------------- optional company CRUD -------------------------- */
export const fetchCompanies = () => fetchJSON(`${API}/companies`);
export const fetchCompany = (id) => fetchJSON(`${API}/companies/${id}`);
export const createCompany = (data) => fetchJSON(`${API}/companies`, { method: "POST", json: data, admin: true });
export const deleteCompany = (id) => fetchOK(`${API}/companies/${id}`, { method: "DELETE", admin: true });

/* ----------------------------- addresses (company) ----------------------------- */
export const getCompanyAddresses = (id) => fetchJSON(`${API}/companies/${id}/addresses`);
export const addCompanyAddress = (id, payload) => fetchJSON(`${API}/companies/${id}/addresses`, { method: "POST", json: payload, admin: true });
export const updateCompanyAddress = (id, addrId, payload) => fetchJSON(`${API}/companies/${id}/addresses/${addrId}`, { method: "PATCH", json: payload, admin: true });
export const deleteCompanyAddress = (id, addrId) => fetchOK(`${API}/companies/${id}/addresses/${addrId}`, { method: "DELETE", admin: true });

/* ------------------------------ bank accounts ------------------------------ */
export const getCompanyBankAccounts = (id) => fetchJSON(`${API}/companies/${id}/bank-accounts`);

export const addCompanyBankAccount = (id, payload) => {
  const clean = { ...payload, country_id: payload.country_id ? Number(payload.country_id) : null };
  return fetchJSON(`${API}/companies/${id}/bank-accounts`, { method: "POST", json: clean, admin: true });
};

export const updateCompanyBankAccount = (id, baId, payload) => {
  const clean = { ...payload, country_id: payload.country_id ? Number(payload.country_id) : null };
  return fetchJSON(`${API}/companies/${id}/bank-accounts/${baId}`, { method: "PATCH", json: clean, admin: true });
};

export const deleteCompanyBankAccount = (id, baId) => fetchOK(`${API}/companies/${id}/bank-accounts/${baId}`, { method: "DELETE", admin: true });

/* ----------------------------- notes & history ----------------------------- */
export const getCompanyNotes = (id) => fetchJSON(`${API}/companies/${id}/notes`);
export const addCompanyNote = (id, payload) => fetchJSON(`${API}/companies/${id}/notes`, { method: "POST", json: payload, admin: true });
export const deleteCompanyNote = (id, noteId) => fetchOK(`${API}/companies/${id}/notes/${noteId}`, { method: "DELETE", admin: true });

export const getCompanyStatusHistory = (id) => fetchJSON(`${API}/companies/${id}/status-history`);

/* -------------------------------- countries -------------------------------- */
export const getCountries = async () => {
  const meta = await fetchJSON(`${API}/meta/options`).catch(() => null);
  const list = meta?.countries || [];
  return list
    .map((c) => ({
      id: c.id != null ? Number(c.id) : null,
      name: c.name || "",
      iso2: (c.iso2 || "").toUpperCase(),
    }))
    .filter((c) => c.id != null && c.name);
};

/* ------------------------------ person addresses ------------------------------ */
export const addPersonAddress = (personId, payload) => fetchJSON(`${API}/persons/${personId}/addresses`, { method: "POST", json: payload, admin: true });
export const updatePersonAddress = (personId, addrId, payload) => fetchJSON(`${API}/persons/${personId}/addresses/${addrId}`, { method: "PATCH", json: payload, admin: true });
export const deletePersonAddress = (personId, addrId) => fetchOK(`${API}/persons/${personId}/addresses/${addrId}`, { method: "DELETE", admin: true });

export const updateCompanyNote = (id, noteId, payload) =>
  fetchJSON(`${API}/companies/${id}/notes/${noteId}`, {
    method: "PATCH",
    json: payload,
    admin: true,
  });

export const getDocKinds = () => fetchJSON(`${API}/meta/doc-kinds`);

export const addCompanyReview = (companyId, body) => apiFetch(`${API}/companies/${companyId}/reviews`, { method: "POST", body });

export const softDeleteCompany = (id, reason) =>
  fetchOK(`${API}/companies/${id}`, {
    method: "DELETE",
    json: reason ? { reason } : undefined,
    admin: true,
  });

export const restoreCompany = (id) =>
  fetchJSON(`${API}/companies/${id}/restore`, {
    method: "POST",
    admin: true,
  });

export const purgeCompany = (id) =>
  fetchOK(`${API}/companies/${id}?purge=1`, {
    method: "DELETE",
    admin: true,
  });

/* ------------------------------- persons api ------------------------------- */
export const getPerson = (id) => fetchJSON(`${API}/persons/${id}`);

export const updatePerson = (id, data) =>
  fetchJSON(`${API}/persons/${id}`, {
    method: "PATCH",
    json: data,
    admin: true,
  });

export const getPersonDocs = (id) => fetchJSON(`${API}/persons/${id}/docs`);

export const addPersonDoc = (id, payload) =>
  fetchJSON(`${API}/persons/${id}/docs`, {
    method: "POST",
    body: toFormData(payload),
    admin: true,
  });

export const updatePersonDoc = (id, docId, payload) =>
  fetchJSON(`${API}/persons/${id}/docs/${docId}`, {
    method: "PATCH",
    body: toFormData(payload),
    admin: true,
  });

export const deletePersonDoc = (id, docId) =>
  fetchOK(`${API}/persons/${id}/docs/${docId}`, {
    method: "DELETE",
    admin: true,
  });

export const getPersonAddresses = (id) => fetchJSON(`${API}/persons/${id}/addresses`);

export const getPersonNotes = (id) => fetchJSON(`${API}/persons/${id}/notes`);

export const addPersonNote = (id, payload) =>
  fetchJSON(`${API}/persons/${id}/notes`, {
    method: "POST",
    json: payload,
    admin: true,
  });

export const updatePersonNote = (id, noteId, payload) =>
  fetchJSON(`${API}/persons/${id}/notes/${noteId}`, {
    method: "PATCH",
    json: payload,
    admin: true,
  });

export const deletePersonNote = (id, noteId) =>
  fetchOK(`${API}/persons/${id}/notes/${noteId}`, {
    method: "DELETE",
    admin: true,
  });

export const getPersonRoles = (id) => fetchJSON(`${API}/persons/${id}/roles`);

export const getPersonStatusHistory = (id) => fetchJSON(`${API}/persons/${id}/status-history`);

export const setPersonStatus = (id, status) =>
  fetchJSON(`${API}/persons/${id}/status`, {
    method: "PATCH",
    json: { status },
    admin: true,
  });

export const softDeletePerson = (id, reason = "") =>
  fetchJSON(`${API}/persons/${id}/soft-delete`, {
    method: "POST",
    json: { reason },
    admin: true,
  });

export const restorePerson = (id) =>
  fetchJSON(`${API}/persons/${id}/restore`, {
    method: "POST",
    admin: true,
  });

export const purgePerson = (id) =>
  fetchOK(`${API}/persons/${id}/purge`, {
    method: "POST",
    admin: true,
  });

export const uploadCocReceipt = (companyId, file, { notes, paidBy } = {}) => {
  if (!companyId || !file) throw new Error("Missing companyId or file");
  const form = new FormData();
  form.append("file", file);
  if (notes) form.append("notes", notes);
  if (paidBy) form.append("paidBy", paidBy);

  return apiFetch(`${API}/companies/${companyId}/coc/receipt`, {
    method: "POST",
    body: form,
    admin: true,
  }).then(async (res) => {
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Upload failed");
    }
    return res.json();
  });
};

export const searchExistingUbos = async (firstName, lastName, country) => {
  try {
    const params = new URLSearchParams({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      ...(country && { country: country.toUpperCase() }),
    });

    const response = await fetchJSON(`${API}/persons/search?${params}`);
    return response.persons || [];
  } catch (error) {
    console.warn("UBO search failed:", error);
    return [];
  }
};

export async function linkTenantOrganization(tenantId, orgId) {
  return apiFetch(`/admin/tenants/${tenantId}/clerk-link`, {
    method: "PATCH",
    json: { orgId },
    portal: true,
  });
}

/* ======================== INVITATION MANAGEMENT ======================== */

export const getPortalInvitations = (options = {}) => {
  const { status, limit = 100, offset = 0 } = options;
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("limit", String(limit));
  params.append("offset", String(offset));

  return fetchJSON(`${API}/admin/invitations?${params}`, { portal: true });
};

export const resendInvitation = (invitationId) => {
  return fetchJSON(`${API}/admin/invitations/${invitationId}/resend`, {
    method: "POST",
    portal: true,
  });
};

export const revokeInvitation = (invitationId) => {
  return fetchJSON(`${API}/admin/invitations/${invitationId}/revoke`, {
    method: "POST",
    portal: true,
  });
};

/* ======================== SUPER ADMIN ENDPOINTS ======================== */

export const fetchSuperAdminDashboardStats = () => {
  return fetchJSON(`${API}/superadmin/dashboard-stats`, {
    portal: true,
  });
};

export const fetchSuperAdminTenants = (options = {}) => {
  const { search, status, limit = 100, offset = 0 } = options;
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (status) params.append("status", status);
  params.append("limit", String(limit));
  params.append("offset", String(offset));

  return fetchJSON(`${API}/superadmin/tenants?${params}`, {
    portal: true,
  });
};

export const updateTenantStatus = (tenantId, status) => {
  return fetchJSON(`${API}/superadmin/tenants/${tenantId}/status`, {
    method: "PATCH",
    json: { status },
    portal: true,
  });
};

export const fetchSuperAdminRecentInvitations = (options = {}) => {
  const { limit = 20 } = options;
  const params = new URLSearchParams();
  params.append("limit", String(limit));

  return fetchJSON(`${API}/superadmin/recent-invitations?${params}`, {
    portal: true,
  });
};

export const fetchSuperAdminTenantMembers = (tenantId) =>
  fetchJSON(`${API}/superadmin/tenants/${encodeURIComponent(tenantId)}/members`, {
    portal: true,
  });

export const patchSuperAdminTenantMember = (tenantId, userId, action, data = {}) => {
  return fetchJSON(`${API}/superadmin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    json: { action, ...data },
    portal: true,
  });
};

export const resendSuperAdminTenantInvitation = (tenantId, invitationId) => {
  return fetchJSON(`${API}/superadmin/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}/resend`, {
    method: "POST",
    portal: true,
  });
};

export const revokeSuperAdminTenantInvitation = (tenantId, invitationId) => {
  return fetchJSON(`${API}/superadmin/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}/revoke`, {
    method: "POST",
    portal: true,
  });
};

/**
 * ============================================================================
 * SECURE DOCUMENT DOWNLOADS - Google Drive Pattern
 * ============================================================================
 *
 * These functions download documents directly with authentication.
 * No pre-signed URLs - requires active session on every request.
 *
 * Returns: Response object (call .blob() to get file data)
 * Usage:
 *   const response = await downloadCompanyDoc(companyId, docId);
 *   const blob = await response.blob();
 *   const url = window.URL.createObjectURL(blob);
 *   window.open(url, "_blank");
 * ============================================================================
 */

export const getCompanyDocSignedUrl = (companyId, docId) =>
  fetchJSON(`${API}/companies/${encodeURIComponent(companyId)}/docs/${encodeURIComponent(docId)}/signed-url`, {
    admin: true,
  });

export const getPortalCompanyDocSignedUrl = (companyId, docId) =>
  fetchJSON(`${API}/portal/companies/${encodeURIComponent(companyId)}/docs/${encodeURIComponent(docId)}/signed-url`, {
    portal: true,
  });

export const getPersonDocSignedUrl = (personId, docId) =>
  fetchJSON(`${API}/persons/${encodeURIComponent(personId)}/docs/${encodeURIComponent(docId)}/signed-url`, {
    admin: true,
  });

/**
 * Download a company document (admin/portal)
 */
export async function downloadCompanyDoc(companyId, docId, options = {}) {
  const { portal = false, admin = false } = options;

  const response = await apiFetch(`${API}/companies/${encodeURIComponent(companyId)}/docs/${encodeURIComponent(docId)}/file`, { method: "GET", portal, admin });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Download failed" }));
    throw new Error(error.error || error.title || "Download failed");
  }

  return response;
}

/**
 * Download a company document from portal
 */
export async function downloadPortalCompanyDoc(companyId, docId) {
  const response = await apiFetch(`${API}/portal/companies/${encodeURIComponent(companyId)}/docs/${encodeURIComponent(docId)}/file`, { method: "GET", portal: true });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Download failed" }));
    throw new Error(error.error || error.title || "Download failed");
  }

  return response;
}

/**
 * Download a person document (admin only)
 */
export async function downloadPersonDoc(personId, docId) {
  const response = await apiFetch(`${API}/persons/${encodeURIComponent(personId)}/docs/${encodeURIComponent(docId)}/file`, { method: "GET", admin: true });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Download failed" }));
    throw new Error(error.error || error.title || "Download failed");
  }

  return response;
}

/**
 * Helper: Download and open file in new tab
 */
export async function downloadAndOpen(downloadFn, options = {}) {
  const { existingWindow = null } = options;
  let newWindow = existingWindow;
  try {
    // Open a window immediately to satisfy iOS popup blockers.
    if (!newWindow || newWindow.closed) {
      newWindow = window.open("", "_blank", "noopener,noreferrer");
    }

    const response = await downloadFn();
    const blob = await response.blob();

    // Create object URL and open in new tab
    const url = window.URL.createObjectURL(blob);

    if (newWindow) {
      newWindow.location.href = url;
    } else {
      window.location.href = url;
    }

    // Clean up object URL after a delay
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);

    return newWindow;
  } catch (error) {
    if (newWindow && !newWindow.closed) {
      newWindow.close();
    }
    console.error("[downloadAndOpen] Error:", error);
    throw error;
  }
}

function showPopupFallback(url) {
  if (typeof document === "undefined") return;
  const existing = document.querySelector(".popup-fallback");
  if (existing) existing.remove();

  const container = document.createElement("div");
  container.className = "popup-fallback";

  const text = document.createElement("span");
  text.textContent = "Popup blocked.";

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.textContent = "Click to open document";

  const close = document.createElement("button");
  close.type = "button";
  close.className = "popup-fallback__close";
  close.textContent = "×";
  close.addEventListener("click", () => container.remove());

  container.append(text, link, close);
  document.body.appendChild(container);
  setTimeout(() => container.classList.add("popup-fallback--show"), 10);
  setTimeout(() => container.remove(), 10000);
}

export async function openDocumentWithSignedUrl(getSignedUrlFn, options = {}) {
  const { notify, fallbackDownload } = options;

  // ✅ MUST be synchronous (user gesture)
  const win = window.open("about:blank", "_blank");

  // If blocked immediately
  if (!win) {
    notify?.("Popup blocked. Click to open document.");
    throw new Error("Popup blocked");
  }

  let loadingShown = false;

  try {
    loadingShown = true;
    notify?.("Opening document...");

    // Fetch signed URL AFTER window is opened
    const res = await getSignedUrlFn();

    if (!res?.url) {
      throw new Error("Invalid signed URL response");
    }

    // Navigate popup
    win.location.href = res.url;
    win.focus?.();

    return true;
  } catch (err) {
    console.error("[openDocumentWithSignedUrl] Failed:", err);

    // Close useless blank tab
    try {
      win.close();
    } catch {}

    if (fallbackDownload) {
      try {
        await fallbackDownload();
        return true;
      } catch (fallbackErr) {
        console.error("[openDocumentWithSignedUrl] Fallback failed:", fallbackErr);
      }
    }

    notify?.(err?.message || "Failed to open document");
    throw err;
  }
}


/* ======================== DOCUMENT WAIVERS ======================== */

/**
 * Get all document waivers for a company
 * Returns array of waivers with doc_kind, reason, waived_by, etc.
 */
export const getCompanyWaivers = (companyId) =>
  fetchJSON(`${API}/companies/${encodeURIComponent(companyId)}/waivers`, {
    admin: true,
  });

/**
 * Create a document waiver
 * Marks a document requirement as not applicable
 *
 * @param {number} companyId - Company ID
 * @param {object} data - Waiver data { doc_kind, reason }
 * @returns {Promise<object>} Created waiver
 */
export const createCompanyWaiver = (companyId, data) => {
  // Validate input
  if (!data.doc_kind || !data.reason) {
    throw new Error("doc_kind and reason are required");
  }

  if (data.reason.trim().length < 10) {
    throw new Error("Reason must be at least 10 characters");
  }

  return fetchJSON(`${API}/companies/${encodeURIComponent(companyId)}/waivers`, {
    method: "POST",
    json: {
      doc_kind: data.doc_kind.trim().toUpperCase(),
      reason: data.reason.trim(),
    },
    admin: true,
  });
};

/**
 * Delete a document waiver
 * Re-enables the document requirement
 *
 * @param {number} companyId - Company ID
 * @param {number} waiverId - Waiver ID
 * @returns {Promise<object>} Success response
 */
export const deleteCompanyWaiver = (companyId, waiverId) =>
  fetchOK(`${API}/companies/${encodeURIComponent(companyId)}/waivers/${encodeURIComponent(waiverId)}`, {
    method: "DELETE",
    admin: true,
  });

export const searchUnified = (params = {}) => search(params);

// These are "file-less" helpers only if you call them with a file.
// Your UI buttons should still pass a File from <input type="file" />.
export const importDBCustomers = (file) => importDatabaseCustomers(file);
export const enrichFinalUpdated = (file) => importFinalUpdated(file);
